import PropTypes from 'prop-types'
import { forwardRef, memo } from 'react'
import BG from 'components/core/BG/BG'
import Divider from 'components/core/Divider/Divider'
import ScrollRegion from 'components/core/ScrollRegion/ScrollRegion'
import useActiveStateAnimationChildren from 'lib/react/hooks/useActiveStateAnimationChildren'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import { useModalContext } from '../../provider/ModalProvider'
import styles from './Body.module.scss'

/**
 * The `Body`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const Body = forwardRef((props, forwardedRef) => {
  const { children, className, footer, header } = props
  const ref = useFallbackRef(forwardedRef)
  const { isModalContentActive: isActive } = useModalContext()

  // Animation
  useActiveStateAnimationChildren(ref, isActive)

  return (
    <>
      <div className={styles.container}>
        <BG className={styles.base} color="c" opacity="none" mask="wide" />
        <div className={styles.wrapper}>
          {header && <div className={styles.header}>{header}</div>}
          <ScrollRegion
            ref={ref}
            className={`${styles.scroll} ${className}`.trimEnd()}
          >
            {children}
          </ScrollRegion>
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>
      </div>
      <div className={styles.cap}>
        <Divider mask="wide" />
      </div>
    </>
  )
})

// The display name
Body.displayName = 'Body'

/** @type {object} */
Body.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  header: PropTypes.node,
  footer: PropTypes.node
}

Body.defaultProps = {
  className: '',
  children: [],
  header: null,
  footer: null
}

// Memoize
export default memo(Body)
